/**
 * Textos legales de Recursos Imprimibles AR — Baby Shower Dulce Recuerdo.
 *
 * ⚠️ BORRADOR. Antes de publicar:
 * 1. Completar `sellerInfo` con los datos reales del vendedor.
 * 2. Configurar `withdrawalFormUrl` (Botón de arrepentimiento) en data/config.ts.
 * 3. Hacer revisar los textos por un profesional.
 * Todo dato vacío se muestra como "PENDIENTE DE CONFIGURACIÓN".
 */

export const PENDING = 'PENDIENTE DE CONFIGURACIÓN'

export const sellerInfo = {
  /** Nombre y apellido o razón social */
  legalName: '',
  cuit: '',
  address: '',
  email: 'recursosimprimibles.ar@gmail.com',
  /** Canal de soporte (WhatsApp, formulario, etc.) */
  supportChannel: 'recursosimprimibles.ar@gmail.com',
}

const p = (value: string) => value || PENDING

export type LegalBlock =
  | { kind: 'heading'; text: string }
  | { kind: 'text'; text: string; label?: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'pending'; text: string }

export type LegalDoc = {
  id: string
  title: string
  /** Bloque especial que el modal renderiza al final */
  action?: 'withdrawal' | 'support'
  blocks: LegalBlock[]
}

const brandName = 'Recursos Imprimibles AR'
const productName = 'Baby Shower Dulce Recuerdo'

/** Términos y privacidad muestran solo la marca y el correo de contacto. */
const sellerBlocks: LegalBlock[] = [
  { kind: 'text', label: 'Marca:', text: brandName },
  { kind: 'text', label: 'Correo electrónico:', text: p(sellerInfo.email) },
]

export const termsDoc: LegalDoc = {
  id: 'terminos',
  title: 'Términos y condiciones',
  blocks: [
    {
      kind: 'text',
      text: `Estos términos regulan la compra del producto digital ${productName}, comercializado bajo la marca ${brandName}. Al realizar una compra, aceptás estas condiciones.`,
    },
    { kind: 'heading', text: '1. Contacto del vendedor' },
    ...sellerBlocks,
    { kind: 'heading', text: '2. Descripción del producto' },
    {
      kind: 'text',
      text: `${productName} es un producto 100% digital. No se envía ningún material físico. La compra incluye:`,
    },
    {
      kind: 'list',
      items: [
        '30 juegos imprimibles para Baby Shower (producto principal).',
        'Bono de regalo: 50 juegos y dinámicas en movimiento.',
        'Versiones rosa y celeste del material.',
      ],
    },
    { kind: 'heading', text: '3. Precio y forma de pago' },
    {
      kind: 'text',
      text: 'El precio es el informado en la sección de oferta de esta página y en el checkout al momento de la compra, expresado en pesos argentinos. Los medios y condiciones de pago son los que ofrece la plataforma de pago utilizada en el checkout.',
    },
    { kind: 'heading', text: '4. Entrega electrónica' },
    {
      kind: 'text',
      text: 'Una vez confirmado el pago, vas a recibir las indicaciones para acceder y descargar los archivos digitales. Te recomendamos guardar una copia de los archivos en tu dispositivo.',
    },
    { kind: 'heading', text: '5. Uso personal y propiedad intelectual' },
    {
      kind: 'text',
      text: `El material es para uso personal y no comercial. Podés imprimirlo las veces que necesites para tus propias celebraciones. No está permitido revender, distribuir, compartir ni publicar los archivos, total o parcialmente. Todos los derechos de propiedad intelectual sobre el material pertenecen a su titular.`,
    },
    { kind: 'heading', text: '6. Garantía y devoluciones' },
    {
      kind: 'text',
      text: 'Ofrecemos una garantía comercial voluntaria de satisfacción de 7 días desde la compra. Esta garantía no limita los derechos que te reconoce la ley. Encontrás el detalle en "Devoluciones y garantía".',
    },
    { kind: 'heading', text: '7. Atención al consumidor' },
    { kind: 'text', label: 'Correo electrónico:', text: p(sellerInfo.email) },
    { kind: 'heading', text: '8. Derechos del consumidor' },
    {
      kind: 'text',
      text: 'Esta compra se rige por la Ley 24.240 de Defensa del Consumidor, el Código Civil y Comercial de la Nación y demás normativa argentina aplicable. Nada de lo dispuesto en estos términos limita los derechos que esa normativa te reconoce, incluido el derecho de revocación, que podés ejercer mediante el Botón de arrepentimiento disponible en esta página.',
    },
  ],
}

export const privacyDoc: LegalDoc = {
  id: 'privacidad',
  title: 'Política de privacidad',
  blocks: [
    {
      kind: 'text',
      text: 'Esta política explica cómo tratamos tus datos personales, conforme a la Ley 25.326 de Protección de Datos Personales.',
    },
    { kind: 'heading', text: '1. Responsable del tratamiento' },
    ...sellerBlocks,
    { kind: 'heading', text: '2. Datos que utilizamos' },
    {
      kind: 'text',
      text: 'Los datos que nos brindás al comprar o al contactarnos, como nombre, correo electrónico y los datos necesarios para procesar la compra y entregar el producto.',
    },
    { kind: 'heading', text: '3. Finalidades' },
    {
      kind: 'list',
      items: [
        'Procesar tu compra y entregarte el producto digital.',
        'Responder tus consultas y brindarte soporte.',
        'Cumplir obligaciones legales.',
      ],
    },
    { kind: 'heading', text: '4. Plataformas externas de pago y entrega' },
    {
      kind: 'text',
      text: 'Los pagos se procesan a través de la plataforma de pago utilizada en el checkout, que trata tus datos de pago según sus propias políticas. No almacenamos los datos de tu tarjeta.',
    },
    { kind: 'heading', text: '5. Tecnologías de medición y publicidad' },
    {
      kind: 'text',
      text: 'Este sitio utiliza el píxel de Meta (Facebook) para medir visitas a la página y la efectividad de nuestros anuncios. Esta herramienta puede usar cookies o tecnologías similares. Podés gestionar tus preferencias de publicidad desde la configuración de tu cuenta de Meta y de tu navegador.',
    },
    { kind: 'heading', text: '6. Seguridad' },
    {
      kind: 'text',
      text: 'Adoptamos medidas razonables para proteger tus datos personales y limitamos su acceso a lo necesario para las finalidades indicadas.',
    },
    { kind: 'heading', text: '7. Tus derechos' },
    {
      kind: 'text',
      text: 'Podés solicitar el acceso, la rectificación, la actualización y la supresión de tus datos personales escribiéndonos al contacto indicado abajo. El derecho de acceso puede ejercerse en forma gratuita a intervalos no inferiores a seis meses, salvo que se acredite un interés legítimo (art. 14, inc. 3, Ley 25.326).',
    },
    {
      kind: 'text',
      text: 'La AGENCIA DE ACCESO A LA INFORMACIÓN PÚBLICA, en su carácter de Órgano de Control de la Ley 25.326, tiene la atribución de atender las denuncias y reclamos que interpongan quienes resulten afectados en sus derechos por incumplimiento de las normas vigentes en materia de protección de datos personales.',
    },
    { kind: 'heading', text: '8. Contacto' },
    { kind: 'text', label: 'Consultas sobre datos personales:', text: p(sellerInfo.email) },
  ],
}

export const refundsDoc: LegalDoc = {
  id: 'devoluciones',
  title: 'Devoluciones y garantía',
  blocks: [
    {
      kind: 'text',
      text: 'Hay dos cosas distintas que te protegen como compradora: nuestra garantía comercial voluntaria y tus derechos legales como consumidora. Una no reemplaza a la otra.',
    },
    { kind: 'heading', text: 'A) Garantía comercial voluntaria de 7 días' },
    {
      kind: 'text',
      text: `Si ${productName} no cumple con tus expectativas, podés solicitar la devolución de tu dinero dentro de los 7 días posteriores a la compra.`,
    },
    { kind: 'text', label: 'Canal para solicitarla:', text: p(sellerInfo.supportChannel) },
    { kind: 'heading', text: 'B) Tus derechos legales como consumidora' },
    {
      kind: 'text',
      text: 'Además de la garantía comercial, conservás todos los derechos que te reconoce la legislación argentina, incluido el derecho de revocación previsto en la Ley 24.240 de Defensa del Consumidor y en el Código Civil y Comercial de la Nación para las compras a distancia, en los plazos y condiciones que establece esa normativa.',
    },
    {
      kind: 'text',
      text: 'Podés ejercer el derecho de revocación mediante el Botón de arrepentimiento disponible en esta página, sin necesidad de registrarte previamente.',
    },
    {
      kind: 'text',
      text: 'La garantía comercial de 7 días es un beneficio adicional y no limita ni reemplaza estos derechos.',
    },
  ],
}

export const withdrawalDoc: LegalDoc = {
  id: 'arrepentimiento',
  title: 'Botón de arrepentimiento',
  action: 'withdrawal',
  blocks: [
    {
      kind: 'text',
      text: 'Si compraste a distancia, podés revocar tu compra dentro de los plazos y condiciones que establece la normativa argentina (Ley 24.240, Código Civil y Comercial de la Nación y Resolución 424/2020 de la Secretaría de Comercio Interior).',
    },
    {
      kind: 'text',
      text: 'Para hacerlo no necesitás registrarte ni crear una cuenta. Una vez enviada tu solicitud, vas a recibir un código de identificación de trámite.',
    },
  ],
}

export const supportDoc: LegalDoc = {
  id: 'contacto',
  title: 'Contacto y soporte',
  action: 'support',
  blocks: [
    {
      kind: 'text',
      text: `¿Tenés dudas sobre tu compra o sobre el acceso a ${productName}? Escribinos y te ayudamos.`,
    },
    { kind: 'text', label: 'Correo electrónico:', text: p(sellerInfo.email) },
    { kind: 'text', label: 'Canal de soporte:', text: p(sellerInfo.supportChannel) },
  ],
}

/** Enlaces legales del footer, en orden. */
export const legalDocs: LegalDoc[] = [termsDoc, privacyDoc, refundsDoc, withdrawalDoc]
